import { useState, useCallback, useEffect } from 'react'
import { ChatMessage } from '../data/mockData'
export interface ChatSession {
  id: string
  title: string
  messages: ChatMessage[]
  createdAt: number
  updatedAt: number
}
const STORAGE_KEY = 'vsm-chat-history'
const MAX_SESSIONS = 20
function loadSessions(): ChatSession[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    return raw ? JSON.parse(raw) : []
  } catch {
    return []
  }
}
function saveSessions(sessions: ChatSession[]) {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify(sessions.slice(0, MAX_SESSIONS)),
    )
  } catch {}
}
function deriveTitle(messages: ChatMessage[]): string {
  const firstUser = messages.find((m) => m.role === 'user')
  if (!firstUser) return 'Nueva conversación'
  const text = firstUser.content.replace(/\*\*/g, '').trim()
  return text.length > 45 ? text.slice(0, 45) + '…' : text
}
export function useChatHistory() {
  const [sessions, setSessions] = useState<ChatSession[]>(loadSessions)
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null)
  const saveSession = useCallback(
    (sessionId: string, messages: ChatMessage[]) => {
      if (messages.filter((m) => m.role === 'user').length === 0) return
      setSessions((prev) => {
        const existing = prev.find((s) => s.id === sessionId)
        let updated: ChatSession[]
        if (existing) {
          updated = prev.map((s) =>
            s.id === sessionId
              ? {
                  ...s,
                  messages,
                  title: deriveTitle(messages),
                  updatedAt: Date.now(),
                }
              : s,
          )
        } else {
          const newSession: ChatSession = {
            id: sessionId,
            title: deriveTitle(messages),
            messages,
            createdAt: Date.now(),
            updatedAt: Date.now(),
          }
          updated = [newSession, ...prev]
        }
        saveSessions(updated)
        return updated
      })
    },
    [],
  )
  const deleteSession = useCallback(
    (sessionId: string) => {
      setSessions((prev) => {
        const updated = prev.filter((s) => s.id !== sessionId)
        saveSessions(updated)
        return updated
      })
      if (activeSessionId === sessionId) setActiveSessionId(null)
    },
    [activeSessionId],
  )
  const clearAll = useCallback(() => {
    setSessions([])
    localStorage.removeItem(STORAGE_KEY)
    setActiveSessionId(null)
  }, [])
  return {
    sessions,
    activeSessionId,
    setActiveSessionId,
    saveSession,
    deleteSession,
    clearAll,
  }
}
